import { useCallback, useEffect, useMemo, useRef } from "react";
import { IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import type { PassChangeReason } from "../models/PassChangeReason";
import { activeColumnDef } from "@/controls/ag-grid/components/ActiveColumn";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import {
	LIST_HEADER_HEIGHT,
	LIST_ROW_HEIGHT,
	listColDef,
} from "@/controls/ag-grid/listGrid";
import {
	OneLineCellStyles,
	LIST_ROW_CLASS,
} from "@/controls/ag-grid/OneLineCells";

interface PassChangeReasonsTableProps {
	reasons: PassChangeReason[];
	selectedReason: PassChangeReason | null;
	onRowSelected: (reason: PassChangeReason | null) => void;
	onDelete: (reason: PassChangeReason) => void;
}

export const PassChangeReasonsTable = ({
	reasons,
	selectedReason,
	onRowSelected,
	onDelete,
}: PassChangeReasonsTableProps) => {
	const gridRef = useRef<AgGridReact<PassChangeReason>>(null);
	const { defaultColDef } = useGridDefinitions({});

	// Clear grid selection when selectedReason has no Id (new item or deselected)
	useEffect(() => {
		if (!selectedReason?.Id && gridRef.current?.api) {
			gridRef.current.api.deselectAll();
		}
	}, [selectedReason]);

	const columnDefs = useMemo<ColDef<PassChangeReason>[]>(
		() => [
			{
				field: "Name",
				headerName: "Name",
				flex: 1,
				minWidth: 120,
			},
			{
				field: "Description",
				headerName: "Description",
				flex: 2,
				minWidth: 150,
			},
			activeColumnDef<PassChangeReason>(),
			{
				headerName: "",
				width: 50,
				minWidth: 50,
				maxWidth: 50,
				sortable: false,
				filter: false,
				resizable: false,
				cellRenderer: (params: { data: PassChangeReason }) => (
					<IconButton
						size="small"
						color="error"
						onClick={(e) => {
							e.stopPropagation();
							if (params.data) {
								onDelete(params.data);
							}
						}}
					>
						<DeleteIcon fontSize="small" />
					</IconButton>
				),
			},
		],
		[onDelete],
	);

	const handleRowClicked = useCallback(
		(event: RowClickedEvent<PassChangeReason>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const getRowClass = useCallback(
		(params: { data: PassChangeReason | undefined }) => {
			const classes: string[] = [];
			if (params.data && selectedReason?.Id === params.data.Id) {
				classes.push("ag-row-selected");
			}
			if (params.data && !params.data.Active) {
				classes.push("ag-row-inactive");
			}
			return classes.join(" ");
		},
		[selectedReason],
	);

	const onCellKeyDown = useArrowRowNavigation<PassChangeReason>(onRowSelected);

	return (
		<>
			<OneLineCellStyles />
			<AgGridReact<PassChangeReason>
				ref={gridRef}
				rowData={reasons}
				columnDefs={columnDefs}
				defaultColDef={listColDef(defaultColDef)}
				enableBrowserTooltips
				rowClass={LIST_ROW_CLASS}
				rowHeight={LIST_ROW_HEIGHT}
				headerHeight={LIST_HEADER_HEIGHT}
				animateRows
				onRowClicked={handleRowClicked}
				onCellKeyDown={onCellKeyDown}
				getRowId={(params) => `${params.data.Id}`}
				getRowClass={getRowClass}
				gridOptions={{
					rowSelection: {
						mode: "singleRow",
						checkboxes: false,
						enableClickSelection: true,
					},
				}}
			/>
		</>
	);
};
